<template>
  <button
    v-bind:class="{
      'is-selected': option.isSelected === true,
      'is-standard': option.isStandard === true,
      'is-multiple': option.isMultiple === true,
    }"
    :aria-labelledby="optionTitle"
    :aria-describedby="optionDescription"
    @click="selectOption(option.id)"
    @keyup.enter="selectOption(option.id)"
    class="c-option"
    tabindex="0"
  >
    <header class="c-option__header">
      <toggle
        :isSelected="option.isSelected"
        class="c-option__toggle"
      />
      <h3
        :id="optionTitle"
        class="c-option__title"
      >
        {{ option.name }} <span v-if="option.isStandard === true">(standard)</span>
      </h3>
      <p
        v-if="visiblePrice !== false"
        class="c-option__price"
      >
        {{ visiblePrice.toLocaleString('nb-NO') }},–
      </p>
    </header>
    <div
      :class="{
        'has-thumbnail': option.thumbnail
      }"
      class="c-option__content"
    >
      <div
        v-if="option.description"
      >
        <p
          v-if="option.description"
          :id="optionDescription"
        >
          {{ option.description }}
        </p>
        <!--<button
          v-if="option.hasExtraInfo"
          @click.stop="showModal"
          class="c-btn"
          type="button"
        >
          Les mer
        </button>-->
      </div>
      <figure
        v-if="option.thumbnail"
        class="c-option__figure"
      >
        <ResponsiveImage
          :id="option.thumbnail"
          :css="'c-option__image'"
        />
      </figure>
    </div>

    <!-- TODO: Modal: Fikse Modal i Option component -->
    <!--<modal
      v-if="option.hasExtraInfo === true"
      v-show="isModalVisible"
      @close="closeModal"
      :content="modalContent"
    />-->
  </button>
</template>

<script>
// VUEX Modules
import Option from '@/models/Option';
import OptionVariable from '@/models/OptionVariable';
import Level from '@/models/Level';

//import Modal from '@/components/Modal.vue';
import Toggle from '@/components/Toggle.vue';
import ResponsiveImage from '@/components/ResponsiveImage.vue';

import find from 'lodash/find';
import includes from 'lodash/includes';

export default {
  name: 'Option',
  components: {
    //Modal,
    Toggle,
    ResponsiveImage,
  },
  props: {
    optionId: {
      type: Number,
      required: true,
    },
    residenceId: {
      type: [
        Number,
        String,
        Boolean,
      ],
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isModalVisible: false,
    };
  },
  computed: {
    option() {
      return Option.query()
        .whereId(this.optionId)
        .withAll()
        .first();
    },
    selectedOption() {
      return Option.query()
        .where('option_category_id', this.option.option_category_id)
        .where('isSelected', true)
        .first();
    },
    variable() {
      return OptionVariable.query()
        .where('residence_id', this.residenceId)
        .where('option_category_id', this.option.option_category_id)
        .first();
    },
    optionLevels() {
      const level = Level.query()
        .whereHas('options', query => {
          query.where('id', this.optionId);
        })
        .get();
      let levels = [];
      level.forEach(item => {
        levels.push(item.id);
      });
      return levels;
    },
    residenceLevels() {
      const level = Level.query()
        .whereHas('residences', query => {
          query.where('id', this.residenceId);
        })
        .get();
      let levels = [];
      level.forEach(item => {
        levels.push(item.id);
      });
      return levels;
    },
    price() {
      return this.option.currentPrice;
    },
    selectedPrice() {
      if (this.selectedOption != null) {
        return this.selectedOption.currentPrice;
      } else {
        return 0;
      }
    },
    visiblePrice() {
      if (this.option.isMultiple === true) {
        if (this.price > 0) {
          return this.price.toLocaleString('nb-NO');
        } else {
          return false;
        }
      }
      if (this.selectedPrice === this.price) {
        return false;
      }
      if (this.selectedPrice > this.price) {
        const cost = this.selectedPrice - this.price;
        return `− ${cost.toLocaleString('nb-NO')}`;
      } else {
        const cost = this.price - this.selectedPrice;
        return `+ ${cost.toLocaleString('nb-NO')}`;
      }
    },
    optionTitle() {
      return 'option-title-' + this.option.id;
    },
    optionDescription() {
      return 'option-description-' + this.option.id;
    },
    modalContent() {
      return {
        title: this.option.extraTitle,
        description: this.option.extraDescription,
        images: this.option.extraImages,
      };
    },
  },
  mounted() {
    this.selectStandardOption();
    this.updateVariable();
  },
  methods: {
    showModal() {
      this.isModalVisible = true;
      this.$store.dispatch('updateModalVisibility', 'open');
    },
    closeModal() {
      this.isModalVisible = false;
      this.$store.dispatch('updateModalVisibility', 'close');
    },
    selectStandardOption() {
      // Reset selection and standard state when mounted.
      Option.update({
        where: this.option.id,
        data: {
          isStandard: false,
          isSelected: false,
        },
      });
      this.residenceLevels.forEach(residenceLevel => {
        if (includes(this.optionLevels, residenceLevel)) {
          Option.update({
            where: this.option.id,
            data: {
              isStandard: true,
              isSelected: true,
            },
          });
        }
      });
    },
    selectOption() {
      // Check if this is a multiselect or single select option
      if (this.option.isMultiple === true) {
        // This is a multi select option
        // Toggle selected state depending on previous state
        if (this.option.isSelected === false) {
          Option.update({
            where: this.option.id,
            data: {
              isSelected: true,
            },
          });
        } else {
          if (this.option.isStandard === false) {
            Option.update({
              where: this.option.id,
              data: {
                isSelected: false,
              },
            });
          }
        }
      } else {
        // This is a single select option
        // Reset all options of the same category to default state
        Option.update({
          where: option => {
            return option.option_category_id === this.option.option_category_id;
          },
          data: {
            isSelected: false,
          },
        });
        // Set current option to selected
        Option.update({
          where: option => {
            return option.id === this.optionId;
          },
          data: {
            isSelected: true,
          },
        });
      }
    },
    updateVariable() {
      // Check if this is a variable price option and if the variable exsist from the residence, if so update with amount and discount
      if (this.option.priceIsVariable === true && this.variable) {
        Option.update({
          where: option => {
            return option.id === this.optionId;
          },
          data: {
            amount: this.variable.amount,
            discount: this.variable.discount,
          },
        });
      } else {
        // Reset back to default state.
        Option.update({
          where: option => {
            return option.id === this.optionId;
          },
          data: {
            amount: 1,
            discount: 0,
          },
        });
      }
    },
  },
};
</script>
